import { formatPrice } from './currencyService';

// Configuration dynamique selon l'environnement
const getApiBaseUrl = () => {
  // En production (Hostinger), utilise le chemin relatif
  if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
    return '/api';
  }
  // En développement local, utilise localhost
  return 'http://localhost/argan-shop/api';
};

const API_BASE_URL = getApiBaseUrl();

// Cache côté client
const translationCache = new Map();

// Fallback translations étendus
const fallbackTranslations = {
  en: {
    // Navigation & UI
    "Accueil": "Home",
    "Boutique": "Shop", 
    "Panier": "Cart",
    "Rechercher": "Search",
    "Connexion": "Login",
    "Inscription": "Register",
    "Mon Compte": "My Account",
    "Déconnexion": "Logout",
    "Ajouter": "Add to Cart",
    "Voir le produit": "View product",
    
    // Catégories
    "Soins Cheveux": "Hair Care",
    "Soins Visage": "Face Care",
    "Soins Corps": "Body Care", 
    "Soins Homme": "Men's Care",
    "Soins Femme": "Women's Care",
    "Huiles Végétales": "Vegetable Oils",
    "Beauté": "Beauty",
    "Homme": "Men",
    "Femme": "Women",
    
    // Produits
    "Shampoing": "Shampoo",
    "Après-shampoing": "Conditioner",
    "Masque Capillaire": "Hair Mask",
    "Sérum": "Serum",
    "Crème": "Cream",
    "Gel": "Gel",
    "Lotion": "Lotion",
    "Huile d'Argan": "Argan Oil",
    "Huile de Figue de Barbarie": "Prickly Pear Oil",
    "Huile de Nigelle": "Black Seed Oil",
    
    // Textes interface
    "Collection Femme": "Women's Collection",
    "Collection Homme": "Men's Collection", 
    "Soins Naturels": "Natural Care",
    "Produits Bio": "Organic Products",
    "Beauté Naturelle": "Natural Beauty",
    "Découvrez nos gammes": "Discover our ranges",
    "Prix": "Price",
    "Nouveau": "New",
    "Promotion": "Sale",
    
    // Pages
    "Notre Univers": "Our Universe",
    "Programme Fidélité": "Loyalty Program",
    "Magazine": "Magazine",
    "Aide": "Help",
    "Contact": "Contact"
  },
  es: {
    "Accueil": "Inicio",
    "Boutique": "Tienda", 
    "Panier": "Carrito",
    "Ajouter": "Añadir al carrito",
    "Soins Cheveux": "Cuidado del Cabello",
    "Soins Visage": "Cuidado Facial",
    "Soins Corps": "Cuidado Corporal",
    "Homme": "Hombres",
    "Femme": "Mujeres",
    "Shampoing": "Champú",
    "Après-shampoing": "Acondicionador",
    "Huile d'Argan": "Aceite de Argán"
  },
  ar: {
    "Accueil": "الرئيسية",
    "Boutique": "المتجر",
    "Panier": "سلة التسوق", 
    "Ajouter": "أضف إلى السلة",
    "Soins Cheveux": "العناية بالشعر",
    "Soins Visage": "العناية بالوجه",
    "Soins Corps": "العناية بالجسم",
    "Homme": "رجال",
    "Femme": "نساء",
    "Huile d'Argan": "زيت الأرغان"
  }
};

// Traduction simple pour les mots français
const getSimpleTranslation = (text, targetLanguage) => {
  if (targetLanguage === 'en') {
    const simpleMap = {
      'shampooing': 'shampoo',
      'après-shampooing': 'conditioner',
      'cheveux': 'hair',
      'visage': 'face',
      'corps': 'body',
      'peau': 'skin',
      'hydratant': 'moisturizing',
      'nourrissant': 'nourishing',
      'réparateur': 'repairing',
      'purifiant': 'purifying',
      'doux': 'gentle',
      'naturel': 'natural',
      'bio': 'organic',
      'huile': 'oil',
      'crème': 'cream',
      'gel': 'gel',
      'lotion': 'lotion',
      'sérum': 'serum',
      'masque': 'mask'
    };
    
    const lowerText = text.toLowerCase();
    for (const [fr, en] of Object.entries(simpleMap)) {
      if (lowerText.includes(fr)) {
        return text.replace(new RegExp(fr, 'gi'), en);
      }
    }
  }
  
  return text;
};

// Détermine la langue source automatiquement
const detectSourceLanguage = (text) => {
  if (!text) return 'fr';
  
  const frenchIndicators = ['soins', 'cheveux', 'visage', 'corps', 'homme', 'femme', 'shampoing', 'après', 'huile', 'visage', 'masque'];
  const englishIndicators = ['care', 'hair', 'face', 'body', 'men', 'women', 'shampoo', 'after', 'oil', 'skin', 'mask'];
  
  const lowerText = text.toLowerCase();
  const frenchScore = frenchIndicators.filter(word => lowerText.includes(word)).length;
  const englishScore = englishIndicators.filter(word => lowerText.includes(word)).length;
  
  return frenchScore > englishScore ? 'fr' : 'en';
};

// FONCTION PRINCIPALE DE TRADUCTION
export const translateText = async (text, targetLanguage) => {
  // Pas besoin de traduction pour texte vide
  if (!text || text.trim().length === 0) {
    return text;
  }

  const cleanText = text.trim();
  
  // Détecter la langue source
  const sourceLanguage = detectSourceLanguage(cleanText);
  console.log(`🌐 Détection: "${cleanText}" → source: ${sourceLanguage}, cible: ${targetLanguage}`);
  
  // Si la langue source et cible sont les mêmes, pas de traduction
  if (sourceLanguage === targetLanguage) {
    console.log(`✅ Même langue: "${cleanText}"`);
    return cleanText;
  }

  const cacheKey = `${cleanText}-${targetLanguage}`;

  // Vérifier le cache
  if (translationCache.has(cacheKey)) {
    return translationCache.get(cacheKey);
  }

  // Vérifier le fallback D'ABORD
  if (fallbackTranslations[targetLanguage] && fallbackTranslations[targetLanguage][cleanText]) {
    const translated = fallbackTranslations[targetLanguage][cleanText];
    translationCache.set(cacheKey, translated);
    console.log(`✅ Fallback: "${cleanText}" → "${translated}"`);
    return translated;
  }

  // Essayer l'API seulement si nécessaire
  try {
    console.log(`🌐 API: "${cleanText}" (${sourceLanguage}) → ${targetLanguage}`);
    
    const response = await fetch(`${API_BASE_URL}/translate.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        text: cleanText,
        targetLanguage,
        sourceLanguage // Ajouter la langue source
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || 'Translation failed');
    }

    const translatedText = data.translation;
    
    // Nettoyer les caractères + résiduels
    const cleanTranslatedText = translatedText.replace(/\+/g, ' ').trim();
    
    translationCache.set(cacheKey, cleanTranslatedText);

    console.log(`✅ API réussi: "${cleanText}" → "${cleanTranslatedText}"`);
    return cleanTranslatedText;

  } catch (error) {
    console.warn(`❌ Échec API "${cleanText}":`, error.message);
    
    // Si l'API échoue, utiliser une traduction simple
    const simpleTranslation = getSimpleTranslation(cleanText, targetLanguage);
    translationCache.set(cacheKey, simpleTranslation);
    return simpleTranslation;
  }
};

// Traduction par lot
export const translateBatch = async (texts, targetLanguage) => {
  // Si la langue cible est la même que la source détectée, pas de traduction
  if (texts.length > 0) {
    const sampleText = texts[0];
    const sourceLanguage = detectSourceLanguage(sampleText);
    if (sourceLanguage === targetLanguage) {
      console.log(`✅ Même langue pour le lot - pas de traduction`);
      return texts;
    }
  }

  // Préparer les résultats avec fallback
  const results = texts.map(text => {
    if (!text || text.trim().length === 0) return text;
    
    const cleanText = text.trim();
    const cacheKey = `${cleanText}-${targetLanguage}`;

    // Vérifier le cache
    if (translationCache.has(cacheKey)) {
      return translationCache.get(cacheKey);
    }

    // Vérifier le fallback
    if (fallbackTranslations[targetLanguage] && fallbackTranslations[targetLanguage][cleanText]) {
      const translated = fallbackTranslations[targetLanguage][cleanText];
      translationCache.set(cacheKey, translated);
      return translated;
    }

    return null; // Marquer pour traduction API
  });

  // Vérifier si tout est déjà traduit
  const needsTranslation = results.some(result => result === null);
  if (!needsTranslation) {
    return results;
  }

  // Préparer les textes à traduire
  const textsToTranslate = texts.filter((text, index) => results[index] === null && text && text.trim().length > 0);

  if (textsToTranslate.length === 0) {
    return results;
  }

  // Traduction par lot
  try {
    const response = await fetch(`${API_BASE_URL}/translate-batch.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        texts: textsToTranslate,
        targetLanguage
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || 'Batch translation failed');
    }

    // Fusionner les résultats et nettoyer les caractères +
    let translationIndex = 0;
    const finalResults = texts.map((text, index) => {
      if (results[index] !== null) {
        return results[index];
      }
      
      if (!text || text.trim().length === 0) {
        return text;
      }

      const translated = data.translations[translationIndex++] || text;
      const cleanTranslated = translated.replace(/\+/g, ' ').trim();
      const cacheKey = `${text.trim()}-${targetLanguage}`;
      translationCache.set(cacheKey, cleanTranslated);
      
      return cleanTranslated;
    });

    console.log(`✅ Lot traduit: ${textsToTranslate.length} textes`);
    return finalResults;

  } catch (error) {
    console.warn('❌ Traduction par lot échouée:', error.message);
    
    // En cas d'erreur, utiliser les traductions simples
    return texts.map((text, index) => {
      if (results[index] !== null) {
        return results[index];
      }
      
      return getSimpleTranslation(text, targetLanguage);
    });
  }
};

// Fonction avec gestion des prix
export const translateTextWithPrice = async (text, targetLanguage, price = null) => {
  if (!text) return text;
  
  let translatedText = await translateText(text, targetLanguage);
  
  // Si un prix est fourni, formater le prix dans la devise de la langue
  if (price !== null && !isNaN(price)) {
    const formattedPrice = formatPrice(price, targetLanguage, 'MAD');
    // Remplacer les occurrences de prix dans le texte traduit
    translatedText = translatedText.replace(/\d+(?:[.,]\d+)?\s*(?:MAD|€|\$)/g, formattedPrice);
  }
  
  return translatedText;
};

// Fonctions utilitaires
export const getTranslationStats = () => {
  return {
    cacheSize: translationCache.size
  };
};

export const clearTranslationCache = () => {
  translationCache.clear();
  console.log('🔄 Cache de traduction vidé');
};

export default translateText;